'use strict';
const rvReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const rvSaveData = Boolean(navigator.connection?.saveData);
class RyvoltCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.index = 0;
    this.paused = rvReducedMotion.matches || rvSaveData || window.matchMedia('(max-width:749px)').matches;
    this.visible = false;
    this.onClick = (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-direction')) this.show(this.index + Number(button.dataset.direction));
      if (button.hasAttribute('data-slide-to')) this.show(Number(button.dataset.slideTo));
      if (button.hasAttribute('data-toggle')) { this.paused = !this.paused; this.sync(); }
    };
    this.addEventListener('click', this.onClick);
    this.onVisibility = () => this.sync();
    document.addEventListener('visibilitychange', this.onVisibility);
    this.observer = new IntersectionObserver((entries) => { this.visible = entries[0].isIntersecting; this.sync(); }, {threshold: 0.15});
    this.observer.observe(this);
    this.addEventListener('focusin', () => { clearTimeout(this.timer); });
    this.addEventListener('focusout', () => { this.schedule(); });
    this.sync();
  }
  show(index) {
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, n) => {
      slide.hidden = n !== this.index;
      if (slide.hidden) slide.querySelectorAll('video').forEach((v) => v.pause());
    });
    this.querySelectorAll('[data-slide-to]').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.slideTo) === this.index)));
    this.sync();
  }
  schedule() {
    clearTimeout(this.timer);
    if (!this.paused && this.visible && !document.hidden && !this.contains(document.activeElement) && this.slides.length > 1) this.timer = setTimeout(() => this.show(this.index + 1), 10000);
  }
  sync() {
    const playing = !this.paused && this.visible && !document.hidden;
    this.slides.forEach((slide, n) => slide.querySelectorAll('video').forEach((video) => {
      if (playing && n === this.index) { video.muted = true; video.play().catch(() => {}); }
      else video.pause();
    }));
    const toggle = this.querySelector('[data-toggle]');
    if (toggle) { toggle.textContent = this.paused ? '▷' : 'Ⅱ'; toggle.setAttribute('aria-label', this.paused ? this.dataset.play : this.dataset.pause); }
    this.schedule();
  }
  disconnectedCallback() { clearTimeout(this.timer); this.observer?.disconnect(); document.removeEventListener('visibilitychange', this.onVisibility); }
}
class RyvoltProduct extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.controller = new AbortController();
    const options = {signal: this.controller.signal};
    this.items = Array.from(this.querySelectorAll('[data-media]'));
    const colourMedia = this.items.filter(item => item.dataset.mediaColor);
    this.galleryAcrossColours = colourMedia.length > 1 && new Set(colourMedia.map(item => item.dataset.mediaColor)).size === colourMedia.length;
    this.addEventListener('dragstart', event => { if (event.target.closest('.rv-product__gallery')) event.preventDefault(); }, options);
    this.addEventListener('click', event => {
      if (Date.now() < (this.suppressClickUntil || 0) && event.target.closest('.rv-product__stage')) { event.preventDefault(); event.stopPropagation(); return; }
      const finish = event.target.closest('[data-color-preview]');
      if (finish) this.showMedia(finish.dataset.colorPreview);
      const thumb = event.target.closest('[data-thumb]');
      if (thumb) this.showMedia(thumb.dataset.thumb);
      const step = event.target.closest('[data-gallery-step]');
      if (step) this.step(Number(step.dataset.galleryStep));
      const variant = event.target.closest('[data-variant]');
      if (!variant || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.selectVariant(variant);
    }, options);
    const stage = this.querySelector('.rv-product__stage');
    stage?.addEventListener('touchstart', event => {
      this.touch = event.touches.length === 1 ? {x:event.touches[0].clientX,y:event.touches[0].clientY} : null;
    }, {...options,passive:true});
    stage?.addEventListener('touchend', event => {
      if (!this.touch || !event.changedTouches.length) return;
      const dx = event.changedTouches[0].clientX - this.touch.x, dy = event.changedTouches[0].clientY - this.touch.y;
      this.touch = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) { this.suppressClickUntil = Date.now() + 500; this.step(dx < 0 ? 1 : -1); }
    }, {...options,passive:true});
    stage?.addEventListener('keydown', event => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.step(event.key === 'ArrowRight' ? 1 : -1); }
    }, options);
    const active = this.items.find(item => !item.hidden) || this.items[0];
    if (active) this.showMedia(active.dataset.media, false);
    const purchase = this.querySelector('.nv-product-enquire, .rv-product__order');
    const mobileCta = this.querySelector('[data-mobile-cta]');
    if (purchase && mobileCta) {
      const updatePurchase = () => {
        cancelAnimationFrame(this.purchaseFrame);
        this.purchaseFrame = requestAnimationFrame(() => {
          mobileCta.hidden = purchase.getBoundingClientRect().bottom > 78;
        });
      };
      window.addEventListener('scroll', updatePurchase, {...options, passive:true});
      window.addEventListener('resize', updatePurchase, options);
      updatePurchase();
    }
  }
  selectVariant(variant, requestedMedia) {
      this.querySelectorAll('[data-variant]').forEach(v => v.setAttribute('aria-current',String(v === variant)));
      const name = this.querySelector('[data-color-name]'); if (name) name.textContent = variant.dataset.label;
      this.querySelectorAll('.rv-product__price-row [data-price], [data-sticky-price]').forEach(price => { price.textContent = variant.dataset.price; });
      const compare = this.querySelector('.rv-product__price-row [data-compare]');
      if (compare) { compare.textContent = variant.dataset.compare; compare.hidden = !variant.dataset.compare; }
      this.querySelectorAll('input[name="id"]').forEach(input => { input.value = variant.dataset.variant; });
      const add = this.querySelector('[data-add-button]');
      if (add) { add.disabled = variant.dataset.available !== 'true'; add.textContent = add.disabled ? this.dataset.unavailable : this.dataset.add; }
      const requested = requestedMedia || variant.dataset.mediaId;
      const mediaId = this.items.some(item => item.dataset.media === requested) ? requested : this.items[0]?.dataset.media;
      if (mediaId) this.showMedia(mediaId, true, false);
      const note = this.querySelector('[data-visual-note]'); if (note) note.hidden = Boolean(requested);
      try { const nextUrl = new URL(location.href); nextUrl.searchParams.set('variant',variant.dataset.variant); history.replaceState(null,'',nextUrl); } catch(error) { if (error.name !== 'SecurityError') throw error; }
      this.dispatchEvent(new CustomEvent('ryvolt:variant',{bubbles:true,detail:{id:variant.dataset.variant,label:variant.dataset.label}}));
  }
  step(direction) {
    const active = this.items.find(item => !item.hidden);
    const group = active?.dataset.mediaColor ? this.items.filter(item => item.dataset.mediaColor === active.dataset.mediaColor) : this.items;
    const items = this.galleryAcrossColours ? this.items : group;
    if (!items.length) return;
    const index = items.indexOf(active);
    this.showMedia(items[(index + direction + items.length) % items.length].dataset.media);
  }
  showMedia(id, prioritize = true, synchronize = true) {
    const items = this.items || Array.from(this.querySelectorAll('[data-media]'));
    const index = items.findIndex(item => item.dataset.media === String(id));
    if (index < 0) return;
    if (synchronize && this.galleryAcrossColours) {
      const match = Array.from(this.querySelectorAll('[data-variant]')).find(button => button.dataset.mediaId === String(id));
      if (match && match.getAttribute('aria-current') !== 'true') { this.selectVariant(match, String(id)); return; }
    }
    items.forEach((item,i) => { item.hidden = i !== index; if (item.hidden) item.querySelectorAll('video').forEach(video => video.pause()); });
    if (prioritize) items[index].querySelectorAll('img').forEach(image => { image.loading = 'eager'; image.fetchPriority = 'high'; });
    const color = items[index].dataset.mediaColor;
    const colorItems = color && !this.galleryAcrossColours ? items.filter(item => item.dataset.mediaColor === color) : items;
    this.querySelectorAll('[data-thumb]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.thumb === String(id)));
      button.hidden = !this.galleryAcrossColours && Boolean(color && button.dataset.thumbColor && button.dataset.thumbColor !== color);
    });
    this.querySelectorAll('[data-color-preview]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.finishName === color)));
    const colorName = this.querySelector('[data-preview-color-name]');
    if (colorName) colorName.textContent = color || '';
    const selectedVariant = this.querySelector('[data-variant][aria-current="true"]');
    const selectedFinish = selectedVariant ? selectedVariant.dataset.label : color;
    this.querySelectorAll('[data-selected-finish]').forEach(name => { name.textContent = selectedFinish || ''; });
    this.querySelectorAll('[data-configuration-link]').forEach(link => {
      const url = new URL(link.href, location.href);
      if (selectedFinish) url.searchParams.set('coloris', selectedFinish);
      else url.searchParams.delete('coloris');
      link.href = url.href;
    });
    if (color) this.dispatchEvent(new CustomEvent('ryvolt:finish', {bubbles:true, detail:{label:color}}));
    this.querySelectorAll('[data-gallery-step]').forEach(button => { button.disabled = colorItems.length < 2; });
    const caption = this.querySelector('[data-gallery-caption]'); if (caption) caption.textContent = items[index].dataset.mediaLabel || '';
    const counter = this.querySelector('[data-gallery-count]'); if (counter) counter.textContent = String(colorItems.indexOf(items[index]) + 1).padStart(2,'0') + ' / ' + String(colorItems.length).padStart(2,'0');
  }
  disconnectedCallback() { this.controller?.abort(); cancelAnimationFrame(this.purchaseFrame); this.initialized = false; }
}
class RyvoltFilm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.video.playsInline = true;
    this.autoSource = () => (rvSaveData || window.matchMedia('(max-width:749px)').matches) ? this.video.dataset.mobileSrc : (this.video.dataset.originalSrc || this.video.dataset.hdSrc);
    this.setSource = (url) => {
      if (!url || this.video.getAttribute('src') === url) return;
      const position = this.video.currentTime || 0;
      const resume = !this.video.paused;
      this.changingQuality = true;
      this.video.addEventListener('loadedmetadata', () => {
        if (position && Number.isFinite(this.video.duration)) this.video.currentTime = Math.min(position, this.video.duration);
        this.changingQuality = false;
        if (resume) this.video.play().catch(() => this.render());
      }, {once:true});
      this.video.src = url;
      this.video.load();
    };
    this.paused = rvReducedMotion.matches || rvSaveData || window.matchMedia('(max-width:749px)').matches;
    this.visible = false;
    this.button = this.querySelector('[data-video-toggle]');
    this.render = () => {
      if (!this.button) return;
      const stopped = this.video.paused;
      this.button.textContent = stopped ? '▷' : 'Ⅱ';
      this.button.setAttribute('aria-label', stopped ? this.dataset.play : this.dataset.pause);
    };
    this.onPlay = () => { this.paused = false; this.autoPausing = false; this.render(); };
    this.onPause = () => { if (!this.autoPausing && !this.changingQuality) this.paused = true; this.autoPausing = false; this.render(); };
    this.onToggle = () => { this.paused = !this.video.paused; this.sync(); };
    this.sync = () => {
      if (this.visible && !this.sourceInitialized) {
        this.sourceInitialized = true;
        this.setSource(this.autoSource());
      }
      if (this.visible && !this.paused && !document.hidden) {
        if (this.video.paused) this.video.play().catch(() => { this.paused = true; this.render(); });
      } else if (!this.video.paused) {
        this.autoPausing = !this.paused;
        this.video.pause();
      }
      this.render();
    };
    this.button?.addEventListener('click', this.onToggle);
    this.video.addEventListener('play', this.onPlay);
    this.video.addEventListener('pause', this.onPause);
    this.observer = new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.2;
      this.sync();
    }, {threshold: [0, 0.2]});
    this.observer.observe(this.video);
    document.addEventListener('visibilitychange', this.sync);
    this.render();
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.sync);
    this.button?.removeEventListener('click', this.onToggle);
    this.video?.removeEventListener('play', this.onPlay);
    this.video?.removeEventListener('pause', this.onPause);
    this.initialized = false;
  }
}
customElements.define('rv-carousel', RyvoltCarousel);
customElements.define('rv-product', RyvoltProduct);
customElements.define('rv-film', RyvoltFilm);
document.addEventListener('click', (event) => {
  const choice = event.target.closest('[data-machine-color]');
  if (!choice || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const machine = choice.closest('.rv-machine');
  if (!machine) return;
  event.preventDefault();
  machine.querySelectorAll('[data-machine-color]').forEach((a) => a.setAttribute('aria-current', String(a === choice)));
  const photo = machine.querySelector('.rv-machine__photo');
  if (photo && choice.dataset.photo) { photo.src = choice.dataset.photo; photo.removeAttribute('srcset'); photo.alt = choice.dataset.label; }
  const name = machine.querySelector('[data-machine-color-name]'); if (name) name.textContent = choice.dataset.label;
  const note = machine.querySelector('[data-machine-color-note]'); if (note) note.hidden = choice.dataset.hasPhoto === 'true';
  const link = machine.querySelector('.rv-machine__button'); if (link) link.href = choice.href;
});

const ryvoltContactMessage = document.getElementById('ContactMessage');
if (ryvoltContactMessage && !ryvoltContactMessage.value.trim()) {
  const params = new URLSearchParams(location.search);
  const piece = params.get('piece'); const model = params.get('modele');
  if (piece) ryvoltContactMessage.value = ryvoltContactMessage.dataset.pieceTemplate + ' « ' + piece.slice(0,120) + ' ».\n';
  else if (model) ryvoltContactMessage.value = ryvoltContactMessage.dataset.modelTemplate + ' « ' + model.slice(0,120) + ' ».\n';
  const color = params.get('coloris');
  if (model && color) ryvoltContactMessage.value += ryvoltContactMessage.dataset.colorTemplate + ' ' + color.slice(0,80) + '.\n';
  if (model && ryvoltContactMessage.dataset.quoteTemplate) ryvoltContactMessage.value += '\n' + ryvoltContactMessage.dataset.quoteTemplate + '\n';
}

class RyvoltColorStory extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.onVariant = (event) => this.select(event.detail.id, event.detail.label);
    this.onClick = (event) => {
      const option = event.target.closest('[data-story-variant]');
      if (!option || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const primary = document.querySelector('rv-product [data-variant="' + option.dataset.storyVariant + '"]');
      if (!primary) return;
      event.preventDefault();
      primary.click();
    };
    this.addEventListener('click', this.onClick);
    document.addEventListener('ryvolt:variant', this.onVariant);
  }
  select(id, label) {
    this.querySelectorAll('[data-story-variant]').forEach((option) => option.setAttribute('aria-current', String(option.dataset.storyVariant === String(id))));
    this.querySelectorAll('[data-color-panel]').forEach((panel) => { panel.hidden = panel.dataset.colorPanel !== String(id); });
    const name = this.querySelector('[data-story-color-name]');
    if (name) name.textContent = label;
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('ryvolt:variant', this.onVariant);
    this.initialized = false;
  }
}
customElements.define('rv-color-story', RyvoltColorStory);

class RyvoltMediaZoom extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.initialized = true;
    this.controller = new AbortController();
    const options = {signal:this.controller.signal};
    this.addEventListener('dragstart',event => { if (event.target.tagName === 'IMG') event.preventDefault(); },options);
    this.addEventListener('click',event => {
      const trigger = event.target.closest('[data-zoom-src]');
      if (trigger && !this.dialog.contains(trigger)) {
        const product = trigger.closest('rv-product');
        if (product && Date.now() < (product.suppressClickUntil || 0)) { event.preventDefault(); return; }
        event.preventDefault();
        this.trigger = trigger;
        const scope = trigger.closest('[data-palette-panel]') || this;
        this.triggers = Array.from(scope.querySelectorAll('[data-zoom-src]'));
        const color = trigger.closest('[data-media]')?.dataset.mediaColor;
        if (color) this.triggers = this.triggers.filter(item => item.closest('[data-media]')?.dataset.mediaColor === color);
        this.index = this.triggers.indexOf(trigger);
        this.savedOverflow = document.body.style.overflow;
        this.render();
        if (!this.dialog.open) this.dialog.showModal();
        document.body.style.overflow = 'hidden';
      }
      if (event.target.closest('[data-zoom-close]')) this.dialog.close();
      const step = event.target.closest('[data-zoom-step]');
      if (step && this.triggers?.length) { this.index = (this.index + Number(step.dataset.zoomStep) + this.triggers.length) % this.triggers.length; this.render(); }
    },options);
    this.dialog.addEventListener('keydown',event => {
      if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && this.triggers?.length) { event.preventDefault(); this.index = (this.index + (event.key === 'ArrowRight' ? 1 : -1) + this.triggers.length) % this.triggers.length; this.render(); }
    },options);
    this.dialog.addEventListener('close',() => { document.body.style.overflow = this.savedOverflow || ''; this.trigger?.focus({preventScroll:true}); },options);
  }
  render() {
    const trigger = this.triggers[this.index];
    const picture = new Image(); picture.src = trigger.dataset.zoomSrc; picture.alt = trigger.dataset.zoomAlt || ''; picture.draggable = false;
    this.dialog.querySelector('[data-zoom-content]').replaceChildren(picture);
    const caption = this.dialog.querySelector('[data-zoom-caption]'); if (caption) caption.textContent = picture.alt;
    const count = this.dialog.querySelector('[data-zoom-count]'); if (count) count.textContent = String(this.index + 1).padStart(2,'0') + ' / ' + String(this.triggers.length).padStart(2,'0');
    this.dialog.querySelectorAll('[data-zoom-step]').forEach(button => { button.hidden = this.triggers.length < 2; });
  }
  disconnectedCallback() { if (this.dialog?.open) document.body.style.overflow = this.savedOverflow || ''; this.controller?.abort(); this.initialized = false; }
}
customElements.define('rv-media-zoom', RyvoltMediaZoom);

class RyvoltPhotoSeries extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.controller = new AbortController();
    const options = {signal:this.controller.signal};
    this.track = this.querySelector('[data-photo-track]');
    this.slides = Array.from(this.querySelectorAll('[data-photo-slide]'));
    this.index = 0;
    this.addEventListener('click',event => { const button = event.target.closest('[data-photo-step]'); if (button) this.show(this.index + Number(button.dataset.photoStep)); },options);
    this.track.addEventListener('scroll',() => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => { this.index = Math.max(0,Math.min(this.slides.length-1,Math.round(this.track.scrollLeft / this.track.clientWidth))); this.sync(); }); },{...options,passive:true});
    this.track.addEventListener('keydown',event => { if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1)); } },options);
    this.addEventListener('dragstart',event => { if (event.target.tagName === 'IMG') event.preventDefault(); },options);
    this.resize = new ResizeObserver(([entry]) => { const width=entry.contentRect.width; if(this.width!==width){this.width=width;this.track.scrollTo({left:this.index*this.track.clientWidth,behavior:'instant'});this.sync();} }); this.resize.observe(this.track);
    this.sync();
  }
  show(index) { this.index = (index + this.slides.length) % this.slides.length; this.track.scrollTo({left:this.index*this.track.clientWidth,behavior:rvReducedMotion.matches?'instant':'smooth'}); this.sync(); }
  sync() { const count=this.querySelector('[data-photo-number]'); if(count) count.textContent=String(this.index+1).padStart(2,'0'); const caption=this.querySelector('[data-photo-caption-live]'); if(caption) caption.textContent=this.slides[this.index]?.dataset.photoCaption||''; const slide=this.slides[this.index];if(slide){const height=Math.ceil(slide.getBoundingClientRect().height);if(height>0&&Math.abs(this.track.clientHeight-height)>1)this.track.style.height=height+'px';} }
  disconnectedCallback() { cancelAnimationFrame(this.frame); this.resize?.disconnect(); this.controller?.abort(); this.initialized=false; }
}
customElements.define('rv-photo-series', RyvoltPhotoSeries);


document.querySelectorAll('rv-carousel .rv-campaign__visual').forEach((surface) => {
  let start;
  surface.addEventListener('touchstart', (event) => {
    if (event.touches.length !== 1 || event.target.closest('video,button,a')) { start = null; return; }
    start = {x:event.touches[0].clientX,y:event.touches[0].clientY};
  }, {passive:true});
  surface.addEventListener('touchend', (event) => {
    if (!start || !event.changedTouches.length) return;
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    start = null;
    if (Math.abs(dx) < 55 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const carousel = surface.closest('rv-carousel');
    if (carousel) { carousel.paused = true; carousel.show(carousel.index + (dx < 0 ? 1 : -1)); return; }
    const product = surface.closest('rv-product');
    const thumbs = Array.from(product.querySelectorAll('[data-thumb]'));
    const index = thumbs.findIndex((thumb) => thumb.getAttribute('aria-pressed') === 'true');
    const next = thumbs[(index + (dx < 0 ? 1 : -1) + thumbs.length) % thumbs.length];
    if (next) product.showMedia(next.dataset.thumb);
  }, {passive:true});
});

class RyvoltColorGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.choices = Array.from(this.querySelectorAll('[data-palette-choice]'));
    this.select = (choice) => {
      this.choices.forEach(button => button.setAttribute('aria-pressed', String(button === choice)));
      this.querySelectorAll('[data-palette-panel]').forEach(panel => { panel.hidden = panel.dataset.palettePanel !== choice.dataset.paletteChoice; });
    };
    this.onClick = (event) => {
      const angle = event.target.closest('[data-palette-angle]');
      if (angle) {
        const panel = angle.closest('[data-palette-panel]');
        panel.querySelectorAll('[data-palette-view]').forEach(view => { view.hidden = view.dataset.paletteView !== angle.dataset.paletteAngle; });
        panel.querySelectorAll('[data-palette-angle]').forEach(button => button.setAttribute('aria-pressed', String(button === angle)));
        return;
      }
      const choice = event.target.closest('[data-palette-choice]');
      if (!choice) return;
      this.select(choice);
      if (choice.dataset.paletteVariant) document.querySelector('rv-product [data-variant="' + choice.dataset.paletteVariant + '"]')?.click();
      else Array.from(document.querySelectorAll('rv-product [data-color-preview]')).find(button => button.dataset.finishName?.toLocaleLowerCase('fr') === choice.dataset.paletteLabel?.toLocaleLowerCase('fr'))?.click();
    };
    this.onVariant = (event) => {
      const choice = this.choices.find(button => button.dataset.paletteVariant === String(event.detail.id));
      if (choice) this.select(choice);
    };
    this.onFinish = (event) => {
      const choice = this.choices.find(button => button.dataset.paletteLabel?.toLocaleLowerCase('fr') === event.detail.label?.toLocaleLowerCase('fr'));
      if (choice) this.select(choice);
    };
    document.addEventListener('ryvolt:finish', this.onFinish);
    this.addEventListener('click', this.onClick);
    document.addEventListener('ryvolt:variant', this.onVariant);
    const initial = document.querySelector('rv-product [data-variant][aria-current="true"]');
    const selectedChoice = initial && this.choices.find(button => button.dataset.paletteVariant === initial.dataset.variant);
    if (selectedChoice) this.select(selectedChoice);
  }
  disconnectedCallback() {
    document.removeEventListener('ryvolt:finish', this.onFinish);
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('ryvolt:variant', this.onVariant);
    this.initialized = false;
  }
}
customElements.define('rv-color-gallery', RyvoltColorGallery);


